/**
 * Pure file-validation helpers shared by the UI store. Kept side-effect free so
 * they can be unit-tested without a DOM.
 */

/** Reject obviously-too-large files before spinning up the worker. */
export const MAX_FILE_BYTES = 150 * 1024 * 1024; // 150 MB

export interface FileLike {
  name: string;
  size: number;
}

/**
 * File extensions this plugin accepts.
 *
 * All are handled by the same pipeline, because the difference between them
 * isn't the one the suffix suggests: an `.eps` or `.ps` is PostScript, a `.ai`
 * saved since CS is a PDF, a `.ai` from before that is PostScript too, and an
 * `.xps` is a zip package gpdl reads. What the converter branches on is the
 * first few bytes — see `toPdf` in eps.ts — so this list is about what a
 * designer is allowed to drop, not about which code path runs.
 */
export const ACCEPTED_EXTENSIONS = ['.ai', '.eps', '.ps', '.xps', '.oxps'];

/** Returns an error message, or `null` when the file is acceptable. */
export function validateAiFile(file: FileLike): string | null {
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return 'Please choose an Illustrator (.ai), EPS (.eps), PostScript (.ps) or XPS (.xps) file.';
  }
  if (file.size === 0) {
    return 'That file is empty.';
  }
  if (file.size > MAX_FILE_BYTES) {
    return `That file is too large (max ${MAX_FILE_BYTES / 1024 / 1024} MB).`;
  }
  return null;
}
